import { CobaltActionBar } from '@cae/cobalt-react/components/action-bar';
import { CobaltButton } from '@cae/cobalt-react/components/button';
import { CobaltLink } from '@cae/cobalt-react/components/link';
import { CobaltSectionMessage } from '@cae/cobalt-react/components/section-message';
import { CobaltSidePanel } from '@cae/cobalt-react/components/side-panel';
import { MediaQuery } from '@cae/cobalt-react/enums';
import { useMediaQuery } from '@cae/cobalt-react/hooks';
import { useHasPermission } from '@contexts/permissions-provider';
import { useUserDetailPanel } from '@features/users/hooks/use-user-detail-panel/useUserDetailPanel';
import { useUserNavigation } from '@features/users/hooks/use-user-navigation/useUserNavigation';
import { ContentLoader } from '@shared/components/content-loader/ContentLoader';
import { InlineFeedback } from '@shared/components/inline-feedback/InlineFeedback';
import { UnsavedChangesModal } from '@shared/components/unsaved-changes-modal/UnsavedChangesModal';
import * as React from 'react';

import type { UserDetailPanelProps } from './UserDetailPanel.types';

import { UserFormFields } from './components/user-form-fields/UserFormFields';
import styles from './UserDetailPanel.module.css';
import {
  CREATE_USER_ERROR_TITLE,
  ERROR_RETRY_MESSAGE,
  UPDATE_USER_ERROR_TITLE,
} from './UserDetailPanel.utils';

const UserDetailPanel = React.memo(
  ({
    mode,
    userId,
    initialData,
    onClose,
  }: Readonly<UserDetailPanelProps>): React.ReactElement => {
    const {
      formData,
      errors,
      apiError,
      showUnsavedModal,
      isSubmitting,
      isEditOrView,
      isSelfUser,
      isLoadingUser,
      isUserError,
      panelTitle,
      isCheckingEmail,
      handleFieldChange,
      handleRoleToggle,
      handleStatusChange,
      handleCloseAttempt,
      handleKeepEditing,
      handleExitDiscard,
      handleSubmit,
      handleEmailBlur,
      handleEmailFocus,
    } = useUserDetailPanel({ mode, userId, initialData, onClose });

    const canRequestChange = useHasPermission('UsersChangeRequest_Add_Org');
    const { navigateToChangeRequest } = useUserNavigation();

    const isMobileAndTablet = useMediaQuery(MediaQuery.MobileAndTablet);

    const actionBar = React.useMemo(() => {
      if (mode === 'view' || isUserError || isSelfUser) return undefined;

      return (
        <CobaltActionBar
          position="right"
          variant="floating"
          primaryButton={
            <CobaltButton
              variant="primary"
              onClick={handleSubmit}
              pending={isSubmitting}
            >
              {mode === 'add' || mode === 'sf-add' ? 'Grant access' : 'Save'}
            </CobaltButton>
          }
          secondaryButton={
            <CobaltButton variant="secondary" onClick={handleCloseAttempt}>
              Cancel
            </CobaltButton>
          }
        />
      );
    }, [
      mode,
      isUserError,
      isSelfUser,
      handleSubmit,
      isSubmitting,
      handleCloseAttempt,
    ]);

    let panelBodyContent: React.ReactElement;

    if (isLoadingUser || isSubmitting) {
      panelBodyContent = <ContentLoader />;
    } else if (isUserError) {
      panelBodyContent = <InlineFeedback type="data-error" />;
    } else {
      panelBodyContent = (
        <div className={styles.panelBody}>
          {apiError && (
            <CobaltSectionMessage
              variant="danger"
              sectionMessageTitle={
                <h3>
                  {mode === 'add' || mode === 'sf-add'
                    ? CREATE_USER_ERROR_TITLE
                    : UPDATE_USER_ERROR_TITLE}
                  <br />
                  {ERROR_RETRY_MESSAGE}
                </h3>
              }
              data-testid="user-detail-error-banner"
              className={styles.userPanelMessage}
            />
          )}

          <UserFormFields
            formData={formData}
            errors={errors}
            mode={mode}
            onFieldChange={handleFieldChange}
            onRoleToggle={handleRoleToggle}
            onStatusChange={handleStatusChange}
            isPersonalInfoDisabled={isEditOrView || mode === 'sf-add'}
            isPermissionsDisabled={mode === 'view' || isSelfUser}
            isCheckingEmail={isCheckingEmail}
            onEmailBlur={handleEmailBlur}
            onEmailFocus={handleEmailFocus}
            personalInfoNote={
              mode === 'edit' && canRequestChange && userId ? (
                <p className="body-02">
                  In order to make changes to the information listed, please
                  submit a{' '}
                  <CobaltLink
                    inline
                    onClick={() => navigateToChangeRequest(userId)}
                  >
                    change request
                  </CobaltLink>
                  .
                </p>
              ) : undefined
            }
          />
        </div>
      );
    }

    return (
      <>
        <CobaltSidePanel
          isOpen
          closeOnNavigation={false}
          showCloseButton
          title={panelTitle}
          onClose={handleCloseAttempt}
          actionBar={actionBar}
          size={isMobileAndTablet ? 'large' : 'small'}
        >
          {panelBodyContent}
        </CobaltSidePanel>

        <UnsavedChangesModal
          isOpen={showUnsavedModal}
          onConfirm={handleExitDiscard}
          onCancel={handleKeepEditing}
        />
      </>
    );
  }
);

UserDetailPanel.displayName = 'UserDetailPanel';

export { UserDetailPanel };
